import React, { useCallback, useState } from 'react';

import * as RE from '~/reducers/experiences';

import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import {
  Badge,
  Box,
  Button,
  Drawer,
  IconButton,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Typography,
} from '@mui/material';

import { ExperienceFilterContent } from '~/components/Experiences/Home/Content/ExperienceFilterContent';
import { FieldValueParams } from '~/components/Experiences/Home/Experiences';
import { FlexWrapper, OutlineButton } from '~/components/Experiences/components';
import { Field } from '~/components/Experiences/components/Fields';

import { FILTER_KEYS, FILTER_RANGE_KEYS, FilterKeys, themes } from '~/consts/experiences';

import * as ES from '~/services/ExperiencesService';

export type FiltersProps = {
  filters: ES.Filters;
  fieldSearchValue: string;
  resetFilters: () => void;
  categories: RE.CategoriesState;
  fetchExperiencesWithFilters: () => void;
  handleSearchFieldValue: (value: string) => void;
  handleFieldValue: (params: FieldValueParams) => void;
  setFiltersByKey: (key: FilterKeys, value: string) => void;
  showGoLiveDateFilter?: boolean;
  setGoLiveDateFilter?: (value: ES.GoLiveDateInWeeks | null) => void;
  searchModeSelected: SearchModeOptions;
  setSearchModeSelected: (option: SelectChangeEvent<SearchModeOptions>) => void;
};

export enum SearchModeOptions {
  ID = 'idIn',
  TITLE = 'title',
  VENDOR = 'vendor',
}

const searchModeOptionsLabels = {
  [SearchModeOptions.ID]: 'ID',
  [SearchModeOptions.TITLE]: 'Title',
  [SearchModeOptions.VENDOR]: 'Vendor',
};

export function ExperiencesFormFilters({
  filters,
  categories,
  resetFilters,
  setFiltersByKey,
  fieldSearchValue,
  handleFieldValue,
  handleSearchFieldValue,
  fetchExperiencesWithFilters,
  showGoLiveDateFilter,
  setGoLiveDateFilter,
  searchModeSelected,
  setSearchModeSelected,
}: FiltersProps) {
  const [isOpen, setIsOpen] = useState(false);

  const countFiltersSelected = useCallback(() => {
    const selectedFilters = Object.entries(filters).filter((item) => {
      if (FILTER_RANGE_KEYS.includes(item[0])) {
        return item[1]?.min || item[1]?.max;
      }
      if (FILTER_KEYS.includes(item[0] as FilterKeys) && item[1]?.length > 0) {
        return item[1];
      }
      if (item[0] === 'goLiveDateInWeeks' && item[1]) {
        return item[1];
      }
    });

    return selectedFilters.length.toString();
  }, [
    filters.categories,
    filters.status,
    filters.price,
    filters.rating,
    filters.providerIn,
    filters.goLiveDateInWeeks,
  ]);

  const applyFilters = useCallback(() => {
    fetchExperiencesWithFilters();
    handleClose();
  }, [filters]);

  const handleClose = () => setIsOpen(false);
  const handleOpen = () => setIsOpen(true);

  return (
    <>
      <Stack direction="row" spacing={2} alignItems="center">
        <Box>
          <Select onChange={setSearchModeSelected} value={searchModeSelected} fullWidth>
            {Object.entries(searchModeOptionsLabels).map(([key, label]) => (
              <MenuItem key={key} value={key}>
                {label}
              </MenuItem>
            ))}
          </Select>
        </Box>

        <Box flexGrow={1}>
          {searchModeSelected == SearchModeOptions.ID && (
            <Field
              autoComplete="off"
              name="experiences-id"
              value={fieldSearchValue || ''}
              onChange={(params) => handleSearchFieldValue(params.value)}
              placeholder="Search experiences by ID separateds by comma"
              fullWidth
            />
          )}

          {searchModeSelected == SearchModeOptions.TITLE && (
            <Field
              autoComplete="off"
              name="experiences-title"
              value={fieldSearchValue || ''}
              onChange={(params) => handleSearchFieldValue(params.value)}
              placeholder="Search experiences by title"
              fullWidth
            />
          )}

          {searchModeSelected == SearchModeOptions.VENDOR && (
            <Field
              autoComplete="off"
              name="experiences-vendor"
              value={fieldSearchValue || ''}
              onChange={(params) => handleSearchFieldValue(params.value)}
              placeholder="Search experiences by vendor"
              fullWidth
            />
          )}
        </Box>

        <Box>
          <FlexWrapper justify="flex-end">
            <Badge badgeContent={countFiltersSelected()} color="secondary">
              <Button variant="contained" onClick={handleOpen}>
                Filters
              </Button>
            </Badge>
          </FlexWrapper>
        </Box>
      </Stack>

      <Drawer open={isOpen} anchor="left" onClose={handleClose} title="Experience Filters">
        <Box display="flex" justifyContent="space-between" alignItems="center" px={2}>
          <Typography variant="h4" color={themes?.primaryBlue}>
            Experience Filters
          </Typography>
          <IconButton onClick={handleClose}>
            <CancelOutlinedIcon fontSize="large" />
          </IconButton>
        </Box>

        <ExperienceFilterContent
          filters={filters}
          categories={categories}
          setFiltersByKey={setFiltersByKey}
          showGoLiveDateFilter={showGoLiveDateFilter}
          setGoLiveDateFilter={setGoLiveDateFilter}
          handleFieldValue={handleFieldValue}
        />
        <FlexWrapper direction="column">
          <FlexWrapper mt="15px">
            <Button title="Apply Filters" onClick={applyFilters} />
          </FlexWrapper>

          <FlexWrapper mt="15px">
            <OutlineButton title="Reset Filters" onClick={resetFilters} borderColor={themes.fourthBlue} />
          </FlexWrapper>

          <FlexWrapper my="15px">
            <OutlineButton title="Cancel" onClick={handleClose} borderColor={themes?.primaryGrey} />
          </FlexWrapper>
        </FlexWrapper>
      </Drawer>
    </>
  );
}

export default ExperiencesFormFilters;
